/**
 * Print preflight for the Print panel preview: what on this page sits at, past or too close to the trim edge.
 * All sizes in page px (96 dpi). `bleed` = 0 when printer's marks are off.
 */
export const SAFE_INSET = 12; // ⅛ in — the usual "keep text inside" margin print shops ask for

const isText = (o) => o && (o.type === 'textbox' || o.type === 'text');
const hasWords = (o) => {
  const p = o.properties || {};
  return !!(String(p.text || '').trim() || String(p.html || '').replace(/<[^>]*>/g, '').trim());
};

export const preflightPage = (page, master, { bleed = 0, safe = SAFE_INSET } = {}) => {
  const W = page?.width || 0, H = page?.height || 0;
  const objs = [...(page?.objects || []), ...(master?.objects || [])].filter((o) => o && o.visible !== false);
  const out = { edge: 0, bleedShort: 0, bleedFull: 0, unsafeText: 0, total: objs.length, bleed, safe };
  objs.forEach((o) => {
    const l = o.x, t = o.y, r = o.x + o.width, b = o.y + o.height;
    const past = l < -0.5 || t < -0.5 || r > W + 0.5 || b > H + 0.5;
    const touches = past || l <= 0.5 || t <= 0.5 || r >= W - 0.5 || b >= H - 0.5;
    if (touches) out.edge += 1;
    if (bleed && past) {
      // runs past the trim on a side but stops before the bleed edge → a white sliver if the trim is a little off
      const short = (l < -0.5 && l > -bleed + 0.5) || (t < -0.5 && t > -bleed + 0.5) || (r > W + 0.5 && r < W + bleed - 0.5) || (b > H + 0.5 && b < H + bleed - 0.5);
      if (short) out.bleedShort += 1; else out.bleedFull += 1;
    }
    if (isText(o) && hasWords(o) && (l < safe || t < safe || r > W - safe || b > H - safe)) out.unsafeText += 1;
  });
  return out;
};

/** Human-readable preflight lines for the preview ("what to expect on paper"). */
export const preflightMessages = (pf, { marksOn, bleedLabel }) => {
  const msgs = [];
  if (marksOn) {
    if (pf.bleedShort) msgs.push({ kind: 'warn', key: 'bleed-short', text: `${pf.bleedShort} object${pf.bleedShort === 1 ? '' : 's'} run${pf.bleedShort === 1 ? 's' : ''} past the page but stop${pf.bleedShort === 1 ? 's' : ''} before the bleed edge — extend ${pf.bleedShort === 1 ? 'it' : 'them'} the full ${bleedLabel} so the trim never shows a white sliver.` });
    if (pf.bleedFull) msgs.push({ kind: 'ok', key: 'bleed-full', text: `${pf.bleedFull} object${pf.bleedFull === 1 ? '' : 's'} bleed${pf.bleedFull === 1 ? 's' : ''} fully into the ${bleedLabel} bleed and will trim cleanly.` });
    if (!pf.bleedShort && !pf.bleedFull && pf.edge) msgs.push({ kind: 'warn', key: 'edge-no-bleed', text: `${pf.edge} object${pf.edge === 1 ? '' : 's'} end${pf.edge === 1 ? 's' : ''} exactly at the page edge — drag ${pf.edge === 1 ? 'it' : 'them'} out to the bleed line if ${pf.edge === 1 ? 'it' : 'they'} should print to the edge.` });
  } else if (pf.edge) {
    msgs.push({ kind: 'info', key: 'edge-marks-off', text: `${pf.edge} object${pf.edge === 1 ? '' : 's'} touch${pf.edge === 1 ? 'es' : ''} the page edge. Desktop printers leave a white border; for edge-to-edge results turn on crop marks and a bleed and have the sheet trimmed.` });
  }
  if (pf.unsafeText) msgs.push({ kind: 'warn', key: 'unsafe-text', text: `${pf.unsafeText} text box${pf.unsafeText === 1 ? '' : 'es'} sit${pf.unsafeText === 1 ? 's' : ''} inside the ⅛ in safe margin — text this close to the edge can be cut off by the trim or the printer's margin.` });
  if (!msgs.length) msgs.push({ kind: 'ok', key: 'clear', text: marksOn ? 'Nothing sits in the safe margin and nothing ends short of the bleed — this page should trim as previewed.' : 'Nothing touches the page edge or the ⅛ in safe margin.' });
  return msgs;
};
